import { useCallback, useRef } from 'react';
import { haptics } from '@/lib/haptics';

interface UseLongPressOptions {
  delay?: number;
  moveThreshold?: number;
  haptic?: boolean;
  disabled?: boolean;
}

/**
 * Native-feeling long-press handler.
 * - Fires `onLongPress` after `delay` ms (default 350) of stationary press.
 * - Cancels if the pointer moves more than `moveThreshold` px (default 10) — so it
 *   never hijacks vertical scroll on mobile.
 * - Cancels on pointerup/pointercancel/scroll.
 * - Returns props you spread onto the target element.
 */
export function useLongPress(
  onLongPress: () => void,
  { delay = 350, moveThreshold = 8, haptic = true, disabled = false }: UseLongPressOptions = {}
) {
  const timerRef = useRef<number | null>(null);
  const startPos = useRef<{ x: number; y: number } | null>(null);
  const firedRef = useRef(false);

  const clear = useCallback(() => {
    if (timerRef.current !== null) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    startPos.current = null;
  }, []);

  const onPointerDown = useCallback(
    (e: React.PointerEvent) => {
      if (disabled) return;
      // Ignore right-click / non-primary buttons
      if (e.pointerType === 'mouse' && e.button !== 0) return;
      firedRef.current = false;
      startPos.current = { x: e.clientX, y: e.clientY };
      timerRef.current = window.setTimeout(() => {
        firedRef.current = true;
        if (haptic) haptics.longPress();
        onLongPress();
        clear();
      }, delay);
    },
    [disabled, onLongPress, delay, haptic, clear]
  );

  const onPointerMove = useCallback(
    (e: React.PointerEvent) => {
      if (!startPos.current) return;
      const dx = Math.abs(e.clientX - startPos.current.x);
      const dy = Math.abs(e.clientY - startPos.current.y);
      if (dx > moveThreshold || dy > moveThreshold) clear();
    },
    [clear, moveThreshold]
  );

  const onPointerUp = useCallback(
    (e: React.PointerEvent) => {
      // If long-press fired, swallow the click that follows
      if (firedRef.current) {
        e.preventDefault();
        e.stopPropagation();
      }
      clear();
    },
    [clear]
  );

  const onPointerCancel = useCallback(() => clear(), [clear]);
  const onPointerLeave = useCallback(() => clear(), [clear]);
  const onContextMenu = useCallback(
    (e: React.MouseEvent) => {
      // Suppress the OS context menu on long-press touch
      if (firedRef.current) e.preventDefault();
    },
    []
  );

  return {
    handlers: {
      onPointerDown,
      onPointerMove,
      onPointerUp,
      onPointerCancel,
      onPointerLeave,
      onContextMenu,
      // Ensure vertical scroll always wins over the long-press timer on touch.
      style: { touchAction: 'pan-y' as const },
    },
    didLongPress: () => firedRef.current,
  };
}
